import React, { useState, useEffect } from 'react';
import { useToast } from '../../../contexts/ToastContext';
import FormModal from '../../common/FormModal';
import ConfirmModal from '../../common/ConfirmModal';
import FormField from '../../common/FormField';
import FormGrid from '../../common/FormGrid';
import { FaSave, FaEdit, FaTrash, FaTimes, FaPlus, FaSearch, FaBoxes, FaTag, FaBalanceScale, FaDollarSign, FaExchangeAlt, FaFileExport, FaSync } from 'react-icons/fa';
import './CatalogoInsumos.css';
import { getApiUrl } from '../../../utils/api';

const CatalogoInsumos = () => {
  const [insumos, setInsumos] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [unidades, setUnidades] = useState([]);
  const [loading, setLoading] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [insumoSeleccionado, setInsumoSeleccionado] = useState(null);
  const [busqueda, setBusqueda] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [insumoAEliminar, setInsumoAEliminar] = useState(null);
  const [showAjusteModal, setShowAjusteModal] = useState(false);
  const [ajustando, setAjustando] = useState(false);
  const [ajusteExistencias, setAjusteExistencias] = useState({});
  const [busquedaAjuste, setBusquedaAjuste] = useState('');
  const toast = useToast();

  // Estado del formulario
  const [formulario, setFormulario] = useState({
    nombre: '',
    idCategoria: '',
    idUnidad: '',
    existencia: 0,
    costo: 0,
    desechable: 0
  });

  // Cargar datos al montar
  useEffect(() => {
    cargarInsumos();
    cargarCategorias();
    cargarUnidades();
  }, []);

  const cargarInsumos = async () => {
    try {
      setLoading(true);
      const response = await fetch(getApiUrl(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tipo: 'getTodosLosIngredientes' })
      });
      const data = await response.json();
      if (data.success) {
        setInsumos(data.data || []);
      } else {
        toast.error(data.message || 'Error al cargar ingredientes');
      }
    } catch (error) {
      console.error('Error al cargar insumos:', error);
      toast.error('Error al cargar los insumos');
    } finally {
      setLoading(false);
    }
  };

  const cargarCategorias = async () => {
    try {
      const response = await fetch(getApiUrl(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tipo: 'getCategoriasIngredientes' })
      });
      const data = await response.json();
      if (data.success) {
        setCategorias(data.data || []);
      }
    } catch (error) {
      console.error('Error al cargar categorías:', error);
    }
  };

  const cargarUnidades = async () => {
    try {
      const response = await fetch(getApiUrl(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tipo: 'getUnidades' })
      });
      const data = await response.json();
      if (data.success) {
        setUnidades(data.data || []);
      }
    } catch (error) {
      console.error('Error al cargar unidades:', error);
    }
  };

  // Manejar cambios en el formulario
  const handleInputChange = (e) => {
    const { name, value, type } = e.target;
    setFormulario({
      ...formulario,
      [name]: type === 'number' ? parseFloat(value) || 0 : value
    });
  };

  // Abrir modal para nuevo insumo
  const nuevoInsumo = () => {
    setInsumoSeleccionado(null);
    setFormulario({
      nombre: '',
      idCategoria: '',
      idUnidad: '',
      existencia: 0,
      costo: 0,
      desechable: 0
    });
    setShowModal(true);
  };

  // Limpiar formulario y cerrar modal
  const handleCloseModal = () => {
    setShowModal(false);
    setInsumoSeleccionado(null);
    setFormulario({
      nombre: '',
      idCategoria: '',
      idUnidad: '',
      existencia: 0,
      costo: 0,
      desechable: 0
    });
  };

  // Seleccionar insumo (solo para resaltar visualmente)
  const seleccionarInsumo = (insumo) => {
    setInsumoSeleccionado(insumo);
  };

  // Editar insumo en modal
  const editarInsumo = (insumo) => {
    setInsumoSeleccionado(insumo);
    setFormulario({
      nombre: insumo.nombre || '',
      idCategoria: insumo.idCategoria || '',
      idUnidad: insumo.idUnidad || '',
      existencia: insumo.existencia || 0,
      costo: insumo.costo || 0,
      desechable: insumo.desechable || 0
    });
    setShowModal(true);
  };

  // Guardar insumo
  const guardarInsumo = async () => {
    if (!formulario.nombre.trim()) {
      toast.error('El nombre del insumo es requerido');
      return;
    }

    if (!formulario.idCategoria) {
      toast.error('La categoría es requerida');
      return;
    }

    if (!formulario.idUnidad) {
      toast.error('La unidad es requerida');
      return;
    }

    try {
      setGuardando(true);

      const esEdicion = !!insumoSeleccionado;
      const payload = {
        tipo: esEdicion ? 'actualizarIngrediente' : 'crearIngrediente',
        nombre: formulario.nombre,
        costo: parseFloat(formulario.costo) || 0,
        idCategoria: parseInt(formulario.idCategoria) || 0,
        idUnidad: parseInt(formulario.idUnidad) || 0,
        existencia: parseFloat(formulario.existencia) || 0,
        desechable: parseInt(formulario.desechable) || 0
      };

      if (esEdicion) {
        payload.idIngrediente = insumoSeleccionado.idIngrediente;
      }

      const response = await fetch(getApiUrl(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await response.json();

      if (data.success) {
        toast.success(esEdicion ? 'Ingrediente actualizado correctamente' : 'Ingrediente creado correctamente');
        handleCloseModal();
        cargarInsumos();
      } else {
        toast.error(data.message || 'Error al guardar el ingrediente');
      }

    } catch (error) {
      console.error('Error al guardar:', error);
      toast.error('Error al guardar el insumo');
    } finally {
      setGuardando(false);
    }
  };

  // Confirmar eliminar insumo
  const confirmarEliminarInsumo = (insumo) => {
    setInsumoAEliminar(insumo);
    setShowDeleteModal(true);
  };

  // Eliminar insumo
  const eliminarInsumo = async () => {
    if (!insumoAEliminar) return;

    try {
      setGuardando(true);
      const response = await fetch(getApiUrl(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tipo: 'eliminarIngrediente',
          idIngrediente: insumoAEliminar.idIngrediente
        })
      });
      const data = await response.json();

      if (data.success) {
        toast.success('Ingrediente eliminado correctamente');
        setShowDeleteModal(false);
        setInsumoAEliminar(null);
        setInsumoSeleccionado(null);
        cargarInsumos();
      } else {
        toast.error(data.message || 'Error al eliminar el ingrediente');
      }
    } catch (error) {
      console.error('Error al eliminar:', error);
      toast.error('Error al eliminar el insumo');
    } finally {
      setGuardando(false);
    }
  };

  // Cerrar modal de confirmación
  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setInsumoAEliminar(null);
  };

  // Filtrar insumos basado en la búsqueda
  const insumosFiltrados = insumos.filter(insumo => {
    if (!busqueda) return true;

    const textoBusqueda = busqueda.toLowerCase();
    return (
      insumo.nombre?.toLowerCase().includes(textoBusqueda) ||
      insumo.nombreCategoria?.toLowerCase().includes(textoBusqueda) ||
      insumo.nombreUnidad?.toLowerCase().includes(textoBusqueda)
    );
  });

  // Abrir modal de ajuste masivo
  const abrirAjusteModal = () => {
    setAjusteExistencias({});
    setBusquedaAjuste('');
    setShowAjusteModal(true);
  };

  const handleExistenciaChange = (idIngrediente, valor) => {
    setAjusteExistencias(prev => ({
      ...prev,
      [idIngrediente]: valor
    }));
  };

  const cantidadCambios = Object.keys(ajusteExistencias).filter(id => {
    const insumo = insumos.find(i => i.idIngrediente === parseInt(id));
    if (!insumo) return false;
    const nuevoValor = parseFloat(ajusteExistencias[id]);
    return !isNaN(nuevoValor) && nuevoValor !== insumo.existencia;
  }).length;

  const guardarAjuste = async () => {
    const ajustes = Object.entries(ajusteExistencias)
      .filter(([id, valor]) => {
        const insumo = insumos.find(i => i.idIngrediente === parseInt(id));
        if (!insumo) return false;
        const nuevoValor = parseFloat(valor);
        return !isNaN(nuevoValor) && nuevoValor !== insumo.existencia;
      })
      .map(([id, valor]) => ({
        idIngrediente: parseInt(id),
        existencia: parseFloat(valor)
      }));

    if (ajustes.length === 0) {
      toast.error('No hay cambios para guardar');
      return;
    }

    try {
      setAjustando(true);
      const response = await fetch(getApiUrl(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tipo: 'ajustarExistenciaIngrediente',
          ajustes: ajustes
        })
      });
      const data = await response.json();
      if (data.success) {
        toast.success(data.message || 'Ajuste realizado correctamente');
        setShowAjusteModal(false);
        cargarInsumos();
      } else {
        toast.error(data.message || 'Error al realizar el ajuste');
      }
    } catch (error) {
      console.error('Error al ajustar:', error);
      toast.error('Error al realizar el ajuste');
    } finally {
      setAjustando(false);
    }
  };

  const insumosAjusteFiltrados = insumos.filter(insumo => {
    if (!busquedaAjuste) return true;
    const texto = busquedaAjuste.toLowerCase();
    return (
      insumo.nombre?.toLowerCase().includes(texto) ||
      insumo.nombreCategoria?.toLowerCase().includes(texto)
    );
  });

  // Exportar insumos a CSV
  const exportarCSV = () => {
    if (insumosFiltrados.length === 0) {
      toast.error('No hay datos para exportar');
      return;
    }

    const headers = ['Nombre', 'Categoría', 'Unidad', 'Existencia', 'Costo', 'Desechable'];
    const rows = insumosFiltrados.map(i => [
      `"${(i.nombre || '').replace(/"/g, '""')}"`,
      `"${(i.nombreCategoria || '').replace(/"/g, '""')}"`,
      `"${(i.nombreUnidad || '').replace(/"/g, '""')}"`,
      (i.existencia || 0).toFixed(3),
      (i.costo || 0).toFixed(2),
      i.desechable ? 'Sí' : 'No'
    ]);

    const csvContent = '\uFEFF' + [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const fecha = new Date().toISOString().split('T')[0];
    link.href = url;
    link.download = `insumos_${fecha}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success(`Se exportaron ${insumosFiltrados.length} registros`);
  };

  // Recalcular costos de platillos
  const [showRecalcularModal, setShowRecalcularModal] = useState(false);
  const [recalculando, setRecalculando] = useState(false);
  const [resultadoRecalculo, setResultadoRecalculo] = useState(null);

  const abrirRecalcularModal = () => {
    setResultadoRecalculo(null);
    setShowRecalcularModal(true);
  };

  const recalcularCostos = async () => {
    try {
      setRecalculando(true);
      const response = await fetch(getApiUrl(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tipo: 'recalcularTodosCostosPlatillos' })
      });
      const data = await response.json();

      if (data.success) {
        setResultadoRecalculo(data.data);
        toast.success(`Se actualizaron ${data.data.preparadosActualizados || 0} preparado(s) y ${data.data.platillosActualizados || 0} platillo(s)`);
      } else {
        toast.error(data.message || 'Error al recalcular costos');
        setShowRecalcularModal(false);
      }
    } catch (error) {
      console.error('Error al recalcular costos:', error);
      toast.error('Error al recalcular costos de platillos');
      setShowRecalcularModal(false);
    } finally {
      setRecalculando(false);
    }
  };

  // Atajos de teclado
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey && e.key === 's') {
        e.preventDefault();
        guardarInsumo();
      }
      if (e.key === 'Escape') {
        if (showModal) {
          handleCloseModal();
        }
      }
      if (e.key === 'Delete' && insumoSeleccionado) {
        confirmarEliminarInsumo(insumoSeleccionado);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [insumoSeleccionado, formulario]);

  return (
    <div className="catalogo-proveedores-excel">
      <div className="excel-header">
        <div className="excel-header-controls">
          <div className="excel-search-container">
            <div className="excel-search-group">
              <FaSearch className="search-icon" />
              <input
                type="text"
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                placeholder="Buscar insumos..."
                className="excel-search-input"
                title="Buscar por nombre, descripción, categoría, etc."
              />
              {busqueda && (
                <button
                  onClick={() => setBusqueda('')}
                  className="excel-clear-search"
                  title="Limpiar búsqueda"
                >
                  <FaTimes />
                </button>
              )}
            </div>
          </div>
          <div className="excel-acciones">
            <button
              onClick={nuevoInsumo}
              className="btn-excel btn-nuevo"
              title="Nuevo insumo"
            >
              <FaPlus /> Nuevo Insumo
            </button>
            <button
              onClick={abrirAjusteModal}
              className="btn-excel btn-ajuste"
              title="Ajustar inventario"
            >
              <FaExchangeAlt /> Ajustar
            </button>
            <button
              onClick={abrirRecalcularModal}
              className="btn-excel"
              style={{ background: 'linear-gradient(135deg, #FF9800, #F57C00)' }}
              title="Recalcular costos de platillos según costos actuales de insumos"
            >
              <FaSync /> Recalcular Costos
            </button>
            <button
              onClick={exportarCSV}
              className="btn-excel btn-exportar"
              title="Exportar a CSV"
            >
              <FaFileExport /> Exportar
            </button>
          </div>
        </div>
      </div>

      {/* Tabla estilo Excel */}
      <div className="excel-table-container">
        <table className="excel-table">
          <thead>
            <tr>
              <th className="excel-th">Nombre</th>
              <th className="excel-th">Categoría</th>
              <th className="excel-th">Unidad</th>
              <th className="excel-th">Existencia</th>
              <th className="excel-th">Costo</th>
              <th className="excel-th">Desechable</th>
              <th className="excel-th excel-th-actions">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="7" className="excel-loading">Cargando ingredientes...</td>
              </tr>
            ) : insumosFiltrados.length === 0 ? (
              <tr>
                <td colSpan="7" className="excel-empty">
                  {busqueda ? `No se encontraron ingredientes que coincidan con "${busqueda}"` : 'No hay ingredientes registrados'}
                </td>
              </tr>
            ) : (
              insumosFiltrados.map(insumo => (
                <tr
                  key={insumo.idIngrediente}
                  className={`excel-row ${
                    insumoSeleccionado?.idIngrediente === insumo.idIngrediente ? 'excel-row-selected' : ''
                  }`}
                  onClick={() => seleccionarInsumo(insumo)}
                >
                  <td className="excel-cell excel-cell-nombre">{insumo.nombre}</td>
                  <td className="excel-cell">{insumo.nombreCategoria}</td>
                  <td className="excel-cell excel-cell-small">{insumo.nombreUnidad} ({insumo.prefijoUnidad})</td>
                  <td className="excel-cell excel-cell-small text-right">{(insumo.existencia || 0).toFixed(3)}</td>
                  <td className="excel-cell excel-cell-small text-right">${(insumo.costo || 0).toFixed(2)}</td>
                  <td className="excel-cell excel-cell-small">
                    {insumo.desechable ? 'Sí' : 'No'}
                  </td>
                  <td className="excel-cell excel-cell-actions">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        editarInsumo(insumo);
                      }}
                      className="excel-btn-action excel-btn-edit"
                      title="Editar"
                    >
                      <FaEdit />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        confirmarEliminarInsumo(insumo);
                      }}
                      className="excel-btn-action excel-btn-delete"
                      title="Eliminar"
                    >
                      <FaTrash />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Footer con información */}
      <div className="excel-footer">
        <div className="excel-info">
          <span>Total de registros: <strong>{insumos.length}</strong></span>
          {busqueda && (
            <span>| Filtrados: <strong>{insumosFiltrados.length}</strong></span>
          )}
          {insumoSeleccionado && (
            <span>| Registro seleccionado: <strong>{insumoSeleccionado.nombre}</strong></span>
          )}
        </div>
        <div className="excel-shortcuts">
          <span>Nuevo: Crear insumo | Enter: Editar | Del: Eliminar</span>
        </div>
      </div>

      {/* Modal para crear/editar insumo */}
      <FormModal
        isOpen={showModal}
        onClose={handleCloseModal}
        title={insumoSeleccionado ? 'Editar Insumo' : 'Nuevo Insumo'}
        onSave={guardarInsumo}
        saveLabel={insumoSeleccionado ? 'Actualizar' : 'Crear'}
        isSaving={guardando}
        size="medium"
        saveIcon={<FaBoxes />}
        maxHeight="45vh"
      >
        <FormGrid columns={2} gap="10px">
          <FormField
            label="Nombre"
            name="nombre"
            value={formulario.nombre}
            onChange={handleInputChange}
            placeholder="Nombre del ingrediente"
            required
            icon={<FaBoxes />}
          />

          <FormField
            label="Categoría"
            type="select"
            name="idCategoria"
            value={formulario.idCategoria}
            onChange={handleInputChange}
            required
            options={categorias.map(categoria => ({
              value: categoria.idCategoria,
              label: categoria.nombre
            }))}
            placeholder="Seleccione categoría"
            icon={<FaTag />}
          />

          <FormField
            label="Unidad"
            type="select"
            name="idUnidad"
            value={formulario.idUnidad}
            onChange={handleInputChange}
            required
            options={unidades.map(unidad => ({
              value: unidad.idUnidad,
              label: `${unidad.nombre} (${unidad.prefijo})`
            }))}
            placeholder="Seleccione unidad"
            icon={<FaBalanceScale />}
          />

          <FormField
            label="Costo"
            type="number"
            name="costo"
            value={formulario.costo}
            onChange={handleInputChange}
            placeholder="0.00"
            step="0.01"
            min="0"
            icon={<FaDollarSign />}
          />

          <FormField
            label="Existencia"
            type="number"
            name="existencia"
            value={formulario.existencia}
            onChange={handleInputChange}
            placeholder="0"
            step="0.01"
            min="0"
          />

          <FormField
            label="Desechable"
            type="select"
            name="desechable"
            value={formulario.desechable}
            onChange={handleInputChange}
            options={[
              { value: 0, label: 'No' },
              { value: 1, label: 'Sí' }
            ]}
          />
        </FormGrid>
      </FormModal>

      {/* Modal de ajuste masivo de inventario */}
      <FormModal
        isOpen={showAjusteModal}
        onClose={() => setShowAjusteModal(false)}
        title={`Ajuste de Inventario${cantidadCambios > 0 ? ` (${cantidadCambios} cambio${cantidadCambios > 1 ? 's' : ''})` : ''}`}
        onSave={guardarAjuste}
        saveLabel={cantidadCambios > 0 ? `Guardar ${cantidadCambios} cambio${cantidadCambios > 1 ? 's' : ''}` : 'Guardar'}
        isSaving={ajustando}
        size="xlarge"
        saveIcon={<FaSave />}
        className="modal-ajuste-inventario"
      >
        <div className="ajuste-search-bar">
          <FaSearch className="ajuste-search-icon" />
          <input
            type="text"
            value={busquedaAjuste}
            onChange={(e) => setBusquedaAjuste(e.target.value)}
            placeholder="Buscar ingrediente..."
            className="ajuste-search-input"
          />
          {busquedaAjuste && (
            <button onClick={() => setBusquedaAjuste('')} className="ajuste-clear-btn">
              <FaTimes />
            </button>
          )}
        </div>
        <div className="ajuste-table-wrapper">
          <table className="ajuste-table">
            <thead>
              <tr>
                <th>Ingrediente</th>
                <th>Categoría</th>
                <th>Unidad</th>
                <th>Existencia Actual</th>
                <th>Nueva Existencia</th>
                <th>Diferencia</th>
              </tr>
            </thead>
            <tbody>
              {insumosAjusteFiltrados.map(insumo => {
                const valorEditado = ajusteExistencias[insumo.idIngrediente];
                const nuevoValor = valorEditado !== undefined ? parseFloat(valorEditado) : null;
                const existenciaActual = insumo.existencia || 0;
                const diferencia = nuevoValor !== null && !isNaN(nuevoValor) ? nuevoValor - existenciaActual : null;
                const hayCambio = diferencia !== null && diferencia !== 0;

                return (
                  <tr key={insumo.idIngrediente} className={hayCambio ? 'ajuste-row-changed' : ''}>
                    <td className="ajuste-cell-nombre">{insumo.nombre}</td>
                    <td>{insumo.nombreCategoria}</td>
                    <td>{insumo.prefijoUnidad}</td>
                    <td className="ajuste-cell-number">{existenciaActual.toFixed(3)}</td>
                    <td className="ajuste-cell-input">
                      <input
                        type="number"
                        step="0.001"
                        min="0"
                        value={valorEditado !== undefined ? valorEditado : ''}
                        onChange={(e) => handleExistenciaChange(insumo.idIngrediente, e.target.value)}
                        placeholder={existenciaActual.toFixed(3)}
                        className={`ajuste-input ${hayCambio ? (diferencia > 0 ? 'ajuste-input-up' : 'ajuste-input-down') : ''}`}
                      />
                    </td>
                    <td className={`ajuste-cell-diff ${hayCambio ? (diferencia > 0 ? 'diff-positive' : 'diff-negative') : ''}`}>
                      {hayCambio ? (diferencia > 0 ? '+' : '') + diferencia.toFixed(3) : '-'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </FormModal>

      {/* Modal de confirmación para eliminar */}
      <ConfirmModal
        isOpen={showDeleteModal}
        onClose={handleCloseDeleteModal}
        onConfirm={eliminarInsumo}
        title="Eliminar Insumo"
        message={`¿Está seguro de eliminar el ingrediente "${insumoAEliminar?.nombre}"? Esta acción no se puede deshacer.`}
        confirmLabel="Eliminar"
        type="danger"
        isLoading={guardando}
      />

      {/* Modal recalcular costos de platillos */}
      <FormModal
        isOpen={showRecalcularModal}
        onClose={() => { setShowRecalcularModal(false); setResultadoRecalculo(null); }}
        title="Recalcular Costos de Platillos"
        onSave={resultadoRecalculo ? () => { setShowRecalcularModal(false); setResultadoRecalculo(null); } : recalcularCostos}
        saveLabel={recalculando ? 'Recalculando...' : resultadoRecalculo ? 'Cerrar' : 'Sí, Recalcular'}
        isSaving={recalculando}
        size={resultadoRecalculo ? 'large' : 'small'}
        saveIcon={resultadoRecalculo ? <FaTimes /> : <FaSync />}
        cancelLabel={resultadoRecalculo ? undefined : 'Cancelar'}
        showCancel={!resultadoRecalculo}
      >
        {!resultadoRecalculo ? (
          <div style={{ textAlign: 'center', padding: '10px 0' }}>
            <div style={{ fontSize: '40px', marginBottom: '12px' }}>💰</div>
            <p style={{ fontSize: '14px', marginBottom: '8px' }}>
              Se recalcularán los costos de <strong>todos los preparados y platillos</strong> basándose en los costos actuales de los insumos.
            </p>
            <p style={{ fontSize: '13px', color: '#555' }}>
              ¿Desea continuar?
            </p>
          </div>
        ) : (
          <div>
            <div style={{
              background: '#e8f5e9', padding: '10px 14px', borderRadius: '6px',
              marginBottom: '12px', fontSize: '13px', color: '#2e7d32', fontWeight: '600'
            }}>
              Se actualizaron {resultadoRecalculo.preparadosActualizados || 0} preparado(s) y {resultadoRecalculo.platillosActualizados || 0} platillo(s)
            </div>
            {resultadoRecalculo.detallePreparados && resultadoRecalculo.detallePreparados.length > 0 && (
              <>
                <strong style={{ fontSize: '12px' }}>Preparados:</strong>
                <div style={{ maxHeight: '150px', overflowY: 'auto', border: '1px solid #ddd', borderRadius: '4px', marginTop: '4px', marginBottom: '12px' }}>
                  <table className="excel-table" style={{ fontSize: '11px' }}>
                    <thead>
                      <tr>
                        <th className="excel-th">Preparado</th>
                        <th className="excel-th" style={{ width: '100px' }}>Anterior</th>
                        <th className="excel-th" style={{ width: '100px' }}>Nuevo</th>
                      </tr>
                    </thead>
                    <tbody>
                      {resultadoRecalculo.detallePreparados.map(p => (
                        <tr key={p.idPreparado}>
                          <td className="excel-cell">{p.nombre}</td>
                          <td className="excel-cell text-right" style={{ color: '#999' }}>${p.costoAnterior.toFixed(2)}</td>
                          <td className="excel-cell text-right" style={{ fontWeight: '600', color: p.nuevoCosto !== p.costoAnterior ? '#c62828' : '#333' }}>${p.nuevoCosto.toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
            {resultadoRecalculo.detallePlatillos && resultadoRecalculo.detallePlatillos.length > 0 && (
              <>
                <strong style={{ fontSize: '12px' }}>Platillos:</strong>
                <div style={{ maxHeight: '250px', overflowY: 'auto', border: '1px solid #ddd', borderRadius: '4px', marginTop: '4px' }}>
                  <table className="excel-table" style={{ fontSize: '11px' }}>
                    <thead>
                      <tr>
                        <th className="excel-th">Platillo</th>
                        <th className="excel-th" style={{ width: '100px' }}>Anterior</th>
                        <th className="excel-th" style={{ width: '100px' }}>Nuevo</th>
                      </tr>
                    </thead>
                    <tbody>
                      {resultadoRecalculo.detallePlatillos.map(p => (
                        <tr key={p.idPlatillo}>
                          <td className="excel-cell">{p.nombre}</td>
                          <td className="excel-cell text-right" style={{ color: '#999' }}>${p.costoAnterior.toFixed(2)}</td>
                          <td className="excel-cell text-right" style={{ fontWeight: '600', color: p.nuevoCosto !== p.costoAnterior ? '#c62828' : '#333' }}>${p.nuevoCosto.toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
            {(!resultadoRecalculo.detallePlatillos || resultadoRecalculo.detallePlatillos.length === 0) &&
             (!resultadoRecalculo.detallePreparados || resultadoRecalculo.detallePreparados.length === 0) && (
              <p style={{ textAlign: 'center', color: '#999', fontSize: '13px' }}>
                No se encontraron platillos ni preparados con recetas configuradas.
              </p>
            )}
          </div>
        )}
      </FormModal>
    </div>
  );
};

export default CatalogoInsumos;